import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/axios";
import WishlistButton from "./WishlistButton";
import { useCart } from "../context/CartContext";
import { Link } from "react-router-dom";
import { useI18n } from "../../i18n/I18nContext";

const ITEMS_PER_PAGE = 12;

function formatMoney(value, currency = "EUR") {
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency }).format(num);
}

function WishlistCard({ product, onRemove }) {
  const navigate = useNavigate();
  const { addItem, openCart } = useCart();
  const { t } = useI18n();
  const [hovered, setHovered] = useState(false);

  const variants = product.variants?.filter((v) => v.is_active !== false) || [];
  const hasMultipleVariants = variants.length > 1;
  const singleVariant = variants.length === 1 ? variants[0] : null;
  const price = product.display_price ?? product.base_price ?? null;

  const handleAdd = (e, variant) => {
    e.stopPropagation();
    addItem({
      id: variant?.id ?? product.id,
      product_id: product.id,
      name: product.name,
      price: Number(variant?.price ?? product.base_price) || 0,
      variant_id: variant?.id ?? null,
      variant_label: variant?.label ?? null,
      variant_type: variant?.is_next ? t("Seguinte") : variant ? t("Base") : null,
      image_url: product.images?.length > 0
        ? product.images[0].image_url
        : product.image_url || null,
      quantity: 1,
    });
    openCart();
  };

  return (
    <div
      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
      onClick={() => navigate(`/products/${product.id}`)}
    >
      {/* Image */}
      <div
        className="relative mb-4 overflow-hidden rounded-xl bg-gray-50"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <img
          src={
            hovered && product.images?.length > 1
              ? product.images[1].image_url
              : product.images?.length > 0
                ? product.images[0].image_url
                : product.image_url || "/placeholder-product.png"
          }
          alt={product.images?.[0]?.alt_text || product.name}
          className="h-48 w-full object-contain pt-8 transition-all duration-500 group-hover:scale-105"
        />
        <div
          className="absolute right-3 top-3 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <WishlistButton
            productId={product.id}
            initialFavorite={true}
            onToggle={(isFavorite) => {
              if (!isFavorite) onRemove(product.id);
            }}
          />
        </div>
      </div>

      {/* Info */}
      <div className="mb-3">
        <h2 className="line-clamp-2 text-lg font-semibold text-gray-900">
          {product.name}
        </h2>
        {product.sku && (
          <p className="mt-1 text-sm text-gray-500">{product.sku}</p>
        )}
      </div>

      {/* Price */}
      {hasMultipleVariants ? (
        <div className="mb-4">
          <p className="text-sm text-gray-500">{t(`${variants.length} variantes disponíveis`)}</p>
          <p className="mt-0.5 text-xs text-gray-400">
            {t("A partir de")} {formatMoney(Math.min(...variants.map((v) => Number(v.price))), variants[0]?.currency || "EUR")}
          </p>
        </div>
      ) : (
        <div className="mb-4">
          <p className="text-2xl font-bold text-gray-900">
            {price !== null
              ? formatMoney(singleVariant ? singleVariant.price : price, singleVariant?.currency || "EUR")
              : t("Sob consulta")}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
        {variants.length > 0 ? (
          <button
            type="button"
            onClick={(e) => singleVariant ? handleAdd(e, singleVariant) : navigate(`/products/${product.id}`)}
            className="w-full rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-gray-800"
          >
            {hasMultipleVariants ? t("Ver variantes") : t("Adicionar ao carrinho")}
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => handleAdd(e, null)}
            className="w-full rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-gray-800"
          >
            {t("Adicionar ao carrinho")}
          </button>
        )}

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); navigate(`/products/${product.id}`); }}
          className="w-full rounded-xl px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100"
        >
          {t("Ver detalhes")}
        </button>
      </div>
    </div>
  );
}

export default function Wishlist() {
  const { t } = useI18n();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [searchName, setSearchName] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const delay = setTimeout(() => setDebouncedSearch(searchName), 400);
    return () => clearTimeout(delay);
  }, [searchName]);

  const fetchWishlist = async (name = "", isFilter = false) => {
    try {
      isFilter ? setFiltering(true) : setLoading(true);
      setError(null);

      const res = await api.get("/api/v1/wishlist", { params: { name } });

      const items = Array.isArray(res.data?.data?.products)
        ? res.data.data.products
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      setWishlist(items);
    } catch (err) {
      console.error("Erro ao carregar wishlist:", err);
      setError(t("Não foi possível carregar a wishlist."));
      setWishlist([]);
    } finally {
      setLoading(false);
      setFiltering(false);
    }
  };

  useEffect(() => { fetchWishlist("", false); }, []);

  useEffect(() => {
    if (debouncedSearch === "" && loading) return;
    fetchWishlist(debouncedSearch, true);
    setPage(0);
  }, [debouncedSearch]);

  const safeWishlist = useMemo(
    () => (Array.isArray(wishlist) ? wishlist : []),
    [wishlist]
  );

  const removeFromWishlist = (productId) => {
    setWishlist((prev) =>
      Array.isArray(prev) ? prev.filter((p) => p.id !== productId) : []
    );
  };

  const totalPages = Math.max(1, Math.ceil(safeWishlist.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, totalPages - 1);
  const visibleProducts = safeWishlist.slice(
    currentPage * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE
  );

  const prevPage = () => setPage((prev) => (prev > 0 ? prev - 1 : totalPages - 1));
  const nextPage = () => setPage((prev) => (prev + 1 >= totalPages ? 0 : prev + 1));

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-xl text-gray-500">{t("A carregar wishlist...")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-xl text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 py-12 px-4 sm:px-6 lg:px-10">
      <div className="mx-auto max-w-7xl">

        <div className="mb-10">
          {/* Breadcrumb */}
          <div className="mb-5 text-sm text-gray-500">
            <Link to="/" className="hover:text-indigo-600 transition">{t("Início")}</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-900 font-medium">Wishlist</span>
          </div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900">{t("A Minha Wishlist")}</h1>
        </div>

        {/* Search bar */}
        <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-sm backdrop-blur sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t("Produtos guardados")}</h2>
            <p className="text-sm text-gray-500">
              {filtering ? t("A filtrar...") : `${safeWishlist.length} ${t("produto(s)")}`}
            </p>
          </div>

          <div className="relative w-full sm:w-72">
            <input
              type="text"
              placeholder={t("Pesquisar produto...")}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 pr-10 text-sm text-gray-700 shadow-sm outline-none transition focus:border-gray-400 focus:ring-4 focus:ring-gray-100"
            />
            {searchName ? (
              <button
                onClick={() => setSearchName("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                </svg>
              </button>
            ) : (
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                </svg>
              </span>
            )}
          </div>
        </div>

        {safeWishlist.length === 0 ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white text-center shadow-sm">
            <div className="mb-3 rounded-full bg-gray-100 p-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800">{t("Wishlist vazia")}</h3>
            <p className="mt-2 max-w-md text-sm text-gray-500">
              {debouncedSearch ? t("Nenhum produto encontrado.") : t("Ainda não adicionaste produtos à tua wishlist.")}
            </p>
          </div>
        ) : (
          <div className={`transition-opacity duration-200 ${filtering ? "opacity-50" : "opacity-100"}`}>
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {visibleProducts.map((product, index) => (
                <WishlistCard
                  key={product.id ?? index}
                  product={product}
                  onRemove={removeFromWishlist}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                <button
                  onClick={prevPage}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-50"
                >
                  {t("Anterior")}
                </button>
                <span className="font-medium text-gray-700">
                  {t(`Página ${currentPage + 1} de ${totalPages}`)}
                </span>
                <button
                  onClick={nextPage}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-50"
                >
                  {t("Próximo")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}